import { useState, useEffect, useCallback, useRef } from "react";

import apiClient from "../../services/apiClient";

// Module-level cache: survives component unmount/remount within the same
// page session. Keyed by a serialization of appliedFilters so a fresh fetch
// only happens when the inputs actually change.
const worklogDataCache = {
  key: null,
  data: null,
  hasLoadedData: false,
};

const buildCacheKey = (appliedFilters, accountId) => {
  if (!accountId) return null;
  try {
    return `${accountId}::${JSON.stringify(appliedFilters)}`;
  } catch {
    return null;
  }
};

const useTimeTrackingData = (appliedFilters, accountId, enabled = false) => {
  const initialKey = buildCacheKey(appliedFilters, accountId);
  const cacheHit = initialKey && worklogDataCache.key === initialKey;

  const [allWorkLogs, setAllWorkLogs] = useState(
    () => (cacheHit ? worklogDataCache.data : [])
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedData, setHasLoadedData] = useState(
    () => (cacheHit ? worklogDataCache.hasLoadedData : false)
  );

  const [progress, setProgress] = useState({
    status: "idle",
    current: 0,
    total: 0,
    percentage: 0,
    message: null,
    issueKey: null,
    phase: "idle",
    cancelRequested: false,
  });
  const [isProgressVisible, setIsProgressVisible] = useState(false);
  const [activeProgressId, setActiveProgressId] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const progressIntervalRef = useRef(null);


  const clearProgressInterval = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const pollProgress = useCallback(
    (progressId) => {
      if (!progressId) return;

      const fetchProgress = async () => {
        try {
          const data = await apiClient.get('/worklog/progress', {
            params: { progress_id: progressId }
          });

          if (data.progress) {
            const current = data.progress.current || 0;
            const total = data.progress.total || 0;
            const status = data.progress.status || "processing";
            const percentage =
              total > 0
                ? Math.min(100, Number(((current / total) * 100).toFixed(1)))
                : status === "completed"
                  ? 100
                  : 0;

            setProgress({
              status,
              current,
              total,
              percentage,
              message: data.progress.message,
              issueKey: data.progress.issue_key,
              phase: data.progress.phase,
              cancelRequested: data.progress.cancel_requested,
            });

            if (["completed", "failed", "cancelled"].includes(status)) {
              clearProgressInterval();
              if (status === "completed") {
                setTimeout(() => setIsProgressVisible(false), 1500);
              }
            }
          }
        } catch (err) {
          console.error("Error polling worklog progress:", err);
        }
      };

      fetchProgress();
      clearProgressInterval();
      progressIntervalRef.current = setInterval(fetchProgress, 1500);
    },
    [clearProgressInterval]
  );

  const loadWorklogData = useCallback(async () => {
    setLoading(true);
    setError(null);
    clearProgressInterval();


    const progressId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `progress-${Date.now()}`;

    setActiveProgressId(progressId);
    setIsProgressVisible(true);
    setProgress({
      status: "initializing",
      current: 0,
      total: 0,
      percentage: 0,
      message: "Preparing worklog request...",
      issueKey: null,
      phase: "initializing",
      cancelRequested: false,
    });

    try {
      const queryParams = new URLSearchParams({
        start_date: appliedFilters.startDate,
        end_date: appliedFilters.endDate,
        start_time: '00:00:00',
        end_time: '23:59:59',
        ...(appliedFilters.worklogAuthor?.length > 0 && {
          worklogAuthor: appliedFilters.worklogAuthor,
        }),
        ...(appliedFilters.issueAssignee?.length > 0 && {
          issueAssignee: appliedFilters.issueAssignee,
        }),
        ...(appliedFilters.project?.length > 0 && {
          project: appliedFilters.project,
        }),
        ...(appliedFilters.status?.length > 0 && {
          status: appliedFilters.status,
        }),
        ...(appliedFilters.priority?.length > 0 && {
          priority: appliedFilters.priority,
        }),
        ...(appliedFilters.sprint?.length > 0 && {
          sprint: appliedFilters.sprint,
        }),
        ...(appliedFilters.startDate && {
          start_date: appliedFilters.startDate,
        }),
        ...(appliedFilters.endDate && { end_date: appliedFilters.endDate }),
        ...(appliedFilters.minHours && { min_hours: appliedFilters.minHours }),
        ...(appliedFilters.maxHours && { max_hours: appliedFilters.maxHours }),
        ...(appliedFilters.searchQuery && {
          search_query: appliedFilters.searchQuery,
        }),
      });

      queryParams.set("progress_id", progressId);

      pollProgress(progressId);

      const result = await apiClient.get(`/worklog?${queryParams.toString()}`);

      // Cancelled requests still return HTTP 200 with error: "cancelled" in body
      if (result.error === "cancelled") {
        setError("Worklog request was cancelled");
        setProgress((prev) => ({
          ...prev,
          status: "cancelled",
          message: "Worklog request cancelled",
          percentage: 0,
          phase: "cancelled",
        }));
        setIsProgressVisible(false);
      } else if (result.error) {
        throw new Error(result.message || result.error || "Failed to load worklog data");
      } else {
        const data = result.data || [];
        setAllWorkLogs(data);
        setHasLoadedData(true);
        setError(null);
        worklogDataCache.key = buildCacheKey(appliedFilters, accountId);
        worklogDataCache.data = data;
        worklogDataCache.hasLoadedData = true;
        setProgress((prev) => ({
          ...prev,
          status: "completed",
          percentage: 100,
          message: "Worklog data loaded",
          phase: "completed",
          cancelRequested: false,
        }));
        setTimeout(() => setIsProgressVisible(false), 1500);
      }
    } catch (error) {
      console.error("Error loading worklog data:", error);
      setError(`Failed to load worklog data: ${error.message}`);
      setAllWorkLogs([]);
      setHasLoadedData(true);
      setProgress((prev) => ({
        ...prev,
        status: "failed",
        message: error.message,
        phase: "failed",
      }));
      setIsProgressVisible(true);
    } finally {
      clearProgressInterval();
      setLoading(false);
      setActiveProgressId(null);
      setIsCancelling(false);
    }
  }, [appliedFilters, clearProgressInterval, pollProgress]);

  const cancelWorklogRequest = useCallback(async () => {
    if (!activeProgressId) return;

    setIsCancelling(true);
    try {
      await apiClient.post('/worklog/cancel', { progress_id: activeProgressId });
    } catch (error) {
      console.error("Error cancelling worklog request:", error);
    } finally {
      setIsCancelling(false);
    }
  }, [activeProgressId]);

  useEffect(() => {
    return () => {
      clearProgressInterval();
    };
  }, [clearProgressInterval]);

  useEffect(() => {
    // Don't auto-load until the user has explicitly applied filters.
    if (!enabled) return;

    // If the cache already has results for these exact filters, reuse them
    // and skip the network call. This is what makes a Dashboard remount
    // (e.g. navigating away and back) instant.
    const key = buildCacheKey(appliedFilters, accountId);
    if (key && worklogDataCache.key === key && worklogDataCache.hasLoadedData) {
      setAllWorkLogs(worklogDataCache.data || []);
      setHasLoadedData(true);
      return;
    }

    loadWorklogData();
  }, [appliedFilters, loadWorklogData, accountId, enabled]);
  
  return {
    allWorkLogs,
    loading,
    error,
    hasLoadedData,
    loadWorklogData,
    progress,
    isProgressVisible,
    cancelWorklogRequest,
    isCancelling,
    hasActiveProgress: Boolean(activeProgressId),
  };
};

export default useTimeTrackingData;
